import { contracts, type MeResponse, type Permission } from '@immo/api-contract';
import { mayCall } from '@/lib/permissions/route-gate';
import { membershipForWorkspace } from './me-membership';

/**
 * Whether the given workspace's membership still has onboarding to finish
 * (IMM-271). Looks up the membership for the resolved active workspace (the same
 * one `loadWorkspaceContext` picks) rather than the first membership, so a
 * multi-workspace member is gated on the workspace they are actually in — a
 * member of one not-onboarded and one onboarded workspace can still enter the
 * onboarded one. A `pending` or `in_progress` status holds the member in
 * onboarding and out of the app; `completed` or a null status (no onboarding
 * record) lets them through. Server-authoritative: callers derive this from a
 * fresh `fetchMe()`, never from cached client state.
 */
export function isOnboardingIncomplete(me: MeResponse, workspaceId: string): boolean {
  const status = membershipForWorkspace(me, workspaceId)?.onboardingStatus ?? null;

  return status === 'pending' || status === 'in_progress';
}

/**
 * Whether the given workspace's MAIN-company onboarding still needs finishing
 * (IMM-322). Looks up the membership for the resolved active workspace (the same
 * one `loadWorkspaceContext` picks) rather than the first membership, so a
 * multi-workspace member is gated on the workspace they are actually in. A
 * `companyOnboardingStatus` of `in_progress` gates the workspace; `completed` or
 * a null status (no company-onboarding record) lets them through. This gate runs
 * AFTER the member-onboarding gate — personal profile first, then company.
 * Server-authoritative: derive from a fresh `fetchMe()`, never cached state.
 */
export function isCompanyOnboardingIncomplete(me: MeResponse, workspaceId: string): boolean {
  const membership = me.organisations.find((entry) => entry.workspaceId === workspaceId) ?? null;

  return membership?.companyOnboardingStatus === 'in_progress';
}

/**
 * Whether entering the given workspace would dead-end the member on the
 * un-bypassable company-blocked screen (IMM-322): its main-company onboarding is
 * still `in_progress` AND the member lacks `workspace:identity:company-update`,
 * so they can neither use the app nor complete the gate. Drives the disabled
 * state of a workspace in the location selector and the onboarding switcher — a
 * member should not be able to strand themselves by switching into it. A member
 * who CAN onboard the company (or whose onboarding is done) is not blocked.
 */
export function isWorkspaceEntryBlocked(me: MeResponse, workspaceId: string): boolean {
  const membership = membershipForWorkspace(me, workspaceId);

  return membership?.companyOnboardingStatus === 'in_progress' && !membership.canOnboardCompany;
}

// Where the workspace gate routes a member who has not yet cleared onboarding.
// `null` means "let them into the app". Member onboarding takes precedence over
// the company gate (personal profile first, then company); the company gate then
// splits on the company-update permission — editors into the wizard, everyone
// else onto the un-bypassable blocking screen (IMM-322).
export type OnboardingGateTarget = '/onboarding' | '/onboarding/company' | '/onboarding/company-blocked' | null;

export function onboardingGateTarget(
  me: MeResponse,
  workspaceId: string,
  permissions: ReadonlyArray<Permission>,
): OnboardingGateTarget {
  if (isOnboardingIncomplete(me, workspaceId)) {
    return '/onboarding';
  }

  if (isCompanyOnboardingIncomplete(me, workspaceId)) {
    return mayCall(permissions, contracts.companyOnboarding.submit)
      ? '/onboarding/company'
      : '/onboarding/company-blocked';
  }

  return null;
}
